import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Inbox, Sparkles, RefreshCw, Image as ImageIcon, Calendar, Hash, ArrowRight, CheckCircle2, X } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';

/**
 * Admin inbox for portal quote requests. Lists every customer-submitted
 * `customer_quote_requests` row with one-click "Convert to Proposal" that
 * deep-links into the AI Assistant with the brief + design pre-filled.
 *
 * Backend:
 *   GET /api/admin/quote-requests?status=new|replied|converted|dismissed
 *   POST /api/admin/quote-requests/{id}/status  {status}
 */

const STATUS_META = {
  new:        { label: 'New',        color: '#0369a1', bg: '#dbeafe' },
  replied:    { label: 'Replied',    color: '#92400e', bg: '#fef3c7' },
  converted:  { label: 'Converted',  color: '#166534', bg: '#dcfce7' },
  dismissed:  { label: 'Dismissed',  color: '#475569', bg: '#f1f5f9' },
};

const formatTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch (_) { return iso; }
};

const QuoteRequests = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('new');
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await axios.get('/admin/quote-requests', { params });
      setRows(data.requests || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load quote requests');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const setStatus = async (id, status) => {
    try {
      await axios.post(`/admin/quote-requests/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const convertToProposal = (row) => {
    // Pre-fill the AI assistant with the customer brief so the admin doesn't
    // re-type it. The AIAssistant page reads `?prefill=` from the URL.
    const seed = [
      `Create a proposal for ${row.customer_name} (${row.customer_email}).`,
      row.quantity ? `Quantity: ${row.quantity}.` : '',
      row.deadline ? `Need-by date: ${row.deadline}.` : '',
      `Project: ${row.description}`,
      row.notes ? `Notes: ${row.notes}` : '',
    ].filter(Boolean).join(' ');
    // Auto-flip status as the admin opens the conversion flow.
    setStatus(row.id, 'converted');
    navigate(`/ai-assistant?prefill=${encodeURIComponent(seed)}`);
  };

  const TABS = [
    { id: 'new',       label: 'New' },
    { id: 'replied',   label: 'Replied' },
    { id: 'converted', label: 'Converted' },
    { id: 'dismissed', label: 'Dismissed' },
    { id: '',          label: 'All' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }} data-testid="quote-requests-page">
      <AppHeader />
      <div style={{ maxWidth: 1300, margin: '0 auto', padding: '28px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <Inbox size={26} color="#0ea5e9" />
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Quote Requests</h1>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>
              Incoming from /portal/new — convert into formal proposals with one click.
            </div>
          </div>
          <button onClick={fetchRows} data-testid="qr-inbox-refresh" style={iconBtn} title="Refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
          {TABS.map((t) => (
            <button
              key={t.id || 'all'}
              onClick={() => setFilter(t.id)}
              data-testid={`qr-tab-${t.id || 'all'}`}
              style={{
                padding: '8px 14px', borderRadius: 999, border: `1px solid ${filter === t.id ? '#0ea5e9' : '#cbd5e1'}`,
                background: filter === t.id ? '#0ea5e9' : '#fff', color: filter === t.id ? '#fff' : '#475569',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3,
              }}
            >
              {t.label}
              {t.id && counts[t.id] != null && (
                <span style={{ marginLeft: 6, padding: '0 6px', borderRadius: 999, background: filter === t.id ? 'rgba(255,255,255,0.25)' : '#e2e8f0', fontSize: 11 }}>
                  {counts[t.id]}
                </span>
              )}
            </button>
          ))}
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          {loading && rows.length === 0 && <div style={empty}>Loading…</div>}
          {!loading && rows.length === 0 && (
            <div style={empty} data-testid="qr-empty">
              <Inbox size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
              <div style={{ fontWeight: 600, color: '#0f172a' }}>No requests in this bucket.</div>
              <div style={{ marginTop: 4, fontSize: 13 }}>When customers submit through /portal/new they&apos;ll show up here.</div>
            </div>
          )}
          {rows.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="qr-list">
              {rows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.new;
                return (
                  <li key={r.id} data-testid={`qr-row-${r.id}`} style={{ display: 'flex', gap: 14, padding: '16px 18px', borderTop: '1px solid #f1f5f9' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <span style={{ fontWeight: 700, color: '#0f172a' }}>{r.customer_name}</span>
                        <span style={{ color: '#64748b', fontSize: 13 }}>· {r.customer_email}</span>
                        <span style={{ ...statusPill, color: meta.color, background: meta.bg }} data-testid={`qr-status-${r.id}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div style={{ color: '#334155', fontSize: 14, marginBottom: 6, lineHeight: 1.5 }}>
                        {r.description}
                      </div>
                      <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
                        {r.quantity && <span><Hash size={12} style={{ verticalAlign: 'text-bottom', marginRight: 3 }} />{r.quantity}</span>}
                        {r.deadline && <span><Calendar size={12} style={{ verticalAlign: 'text-bottom', marginRight: 3 }} />Need by {r.deadline}</span>}
                        {r.has_design && <span><ImageIcon size={12} style={{ verticalAlign: 'text-bottom', marginRight: 3 }} />Has design</span>}
                        <span>· {formatTime(r.created_at)}</span>
                      </div>
                      {r.notes && (
                        <div style={{ marginTop: 6, fontSize: 12, color: '#7c2d12', background: '#fef3c7', border: '1px solid #fde68a', padding: '6px 10px', borderRadius: 6 }}>
                          <strong>Notes:</strong> {r.notes}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                      <Button
                        size="sm"
                        onClick={() => convertToProposal(r)}
                        data-testid={`qr-convert-${r.id}`}
                        style={{ background: '#c2410c', color: '#fff', fontWeight: 700 }}
                      >
                        <Sparkles size={14} style={{ marginRight: 4 }} /> Convert <ArrowRight size={12} style={{ marginLeft: 4 }} />
                      </Button>
                      {r.status !== 'replied' && (
                        <button onClick={() => setStatus(r.id, 'replied')} data-testid={`qr-replied-${r.id}`} style={ghostBtn}>
                          <CheckCircle2 size={12} style={{ marginRight: 4 }} /> Mark replied
                        </button>
                      )}
                      {r.status !== 'dismissed' && (
                        <button onClick={() => setStatus(r.id, 'dismissed')} data-testid={`qr-dismiss-${r.id}`} style={ghostBtn}>
                          <X size={12} style={{ marginRight: 4 }} /> Dismiss
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const empty = { padding: 36, color: '#64748b', textAlign: 'center' };
const iconBtn = { background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#475569', marginLeft: 'auto' };
const statusPill = { padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 0.3 };
const ghostBtn = { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 11, padding: 2, display: 'flex', alignItems: 'center' };

export default QuoteRequests;
